import type { EffectDef } from './types';

/**
 * The effect registry (engine.html §3 "Effects"): registry v1's 20 types plus card 19's stat effects (`train`, `apply_effect`), each with
 * exactly one typed block (ADR 0029's pattern), who runs it and its caps. A port of shared-cs `Story/EffectRegistry.cs` (S1-ENG) for the
 * forms and the validator.
 */
export const EFFECT_TYPES_V1 = [
  'give_items',
  'take_items',
  'trade',
  'reveal_stash',
  'injure',
  'condition',
  'kill',
  'turn',
  'recruit',
  'spawn',
  'wave',
  'noise',
  'objective',
  'flag',
  'mission',
  'tip',
  'ally',
  'boon',
  'delay',
  'end',
] as const;

/** shared-cs `EffectTypes.Stats` (card 19): server-only, beat-only (scenarios queue registry v1 only), no client capability needed. */
export const STAT_EFFECT_TYPES = ['train', 'apply_effect'] as const;

export const EFFECT_TYPES = [...EFFECT_TYPES_V1, ...STAT_EFFECT_TYPES] as const;
export type EffectType = (typeof EFFECT_TYPES)[number];
export type EffectBlockKey = Exclude<keyof EffectDef, 'type'>;

/** The block each type carries (`give_items` → `giveItems`). */
export const EFFECT_BLOCKS: Record<EffectType, EffectBlockKey> = {
  give_items: 'giveItems',
  take_items: 'takeItems',
  trade: 'trade',
  reveal_stash: 'revealStash',
  injure: 'injure',
  condition: 'condition',
  kill: 'kill',
  turn: 'turn',
  recruit: 'recruit',
  spawn: 'spawn',
  wave: 'wave',
  noise: 'noise',
  objective: 'objective',
  flag: 'flag',
  mission: 'mission',
  tip: 'tip',
  ally: 'ally',
  boon: 'boon',
  delay: 'delay',
  end: 'end',
  train: 'train',
  apply_effect: 'applyEffect',
};

/** Who runs it: the phone in the raid, the server on a validated submit, or both. */
export const EFFECT_RUNS: Record<EffectType, 'raid' | 'server' | 'both'> = {
  give_items: 'both',
  take_items: 'raid',
  trade: 'both',
  reveal_stash: 'both',
  injure: 'raid',
  condition: 'server',
  kill: 'raid',
  turn: 'raid',
  recruit: 'both',
  spawn: 'raid',
  wave: 'raid',
  noise: 'raid',
  objective: 'raid',
  flag: 'server',
  mission: 'server',
  tip: 'server',
  ally: 'server',
  boon: 'both',
  delay: 'raid',
  end: 'raid',
  train: 'server',
  apply_effect: 'server',
};

/** Per-path caps (`EffectSpec.PerPath`): at most this many of the type on one path through the chain. */
export const EFFECT_PER_PATH: Partial<Record<EffectType, number>> = {
  reveal_stash: 1,
  recruit: 1,
  wave: 2,
  mission: 1,
  tip: 1,
  ally: 1,
  boon: 1,
  delay: 2,
  train: 2,
  apply_effect: 1,
};

/** Numeric caps (engine.html §3). */
export const EFFECT_CAPS = {
  maxPerOutcome: 8,
  flagsPerOutcome: 4,
  giveStacks: 3,
  stackQty: 10,
  takeQty: 10,
  tradeGive: 2,
  tradePromised: 3,
  /** `TradeRules.MaxPromisedQty`. */
  promisedQty: 100,
  injureHp: 60,
  spawnCount: 8,
  noiseRadiusM: 60,
  boonHeal: 40,
  /** WIND (contract 3.7): `injure.wind` (hit wind) and `boon.wind` (restored), 0..100. */
  injureWind: 100,
  boonWind: 100,
  delayAfterMin: 3,
  delayAfterMax: 120,
} as const;

export const TRADE_RESULTS = ['honest', 'generous', 'short', 'lying'] as const;
export const TRADE_LIES = ['junk', 'ambush'] as const;
export const GIVE_TO = ['pack', 'home'] as const;
export const OBJECTIVE_OPS = ['add', 'complete', 'fail'] as const;
export const FLAG_SCOPES = ['run', 'account'] as const;
export const TIP_MARKERS = ['cache', 'stash_site'] as const;
export const TIP_NEAR = ['player', 'building'] as const;
export const ALLY_OPS = ['add', 'cool', 'end'] as const;
export const ALLY_DISPOSITIONS = ['friendly', 'wary'] as const;
export const DELAY_ON_RAID_END = ['drop', 'carry'] as const;
export const END_AS = ['extracted', 'failed'] as const;
export const EFFECT_CONDITION_IDS = ['wounded', 'laid_up', 'sick', 'bitten'] as const;

/** A fresh block for a type, with the C# defaults. */
export function effectBlockDefaults(type: EffectType): Record<string, unknown> {
  switch (type) {
    case 'give_items':
      return { stacks: [], to: 'pack' };
    case 'take_items':
      return { stacks: [], tag: '', qty: 0 };
    case 'trade':
      return { give: [], promised: [], result: 'honest', bonusTable: '', lie: '', junkItemId: '' };
    case 'reveal_stash':
      return { anchor: '', table: '', rolls: 1 };
    case 'injure':
      return { role: '', hp: 10, wind: null, down: false };
    case 'condition':
      return { role: '', conditionId: 'wounded', turnHours: 0 };
    case 'kill':
      return { role: '' };
    case 'turn':
      return { role: '', zombieId: '', grab: false };
    case 'recruit':
      return { role: '', roleId: '' };
    case 'spawn':
      return { zombieId: '', count: 1, at: 'room', aware: false };
    case 'wave':
      return { size: 4, waveId: '', from: '' };
    case 'noise':
      return { radiusM: 20, at: '' };
    case 'objective':
      return { op: 'complete', objectiveId: '' };
    case 'flag':
      return { key: '', value: '', scope: 'run' };
    case 'mission':
      return { missionId: '' };
    case 'tip':
      return { marker: 'cache', near: 'player', maxDistanceM: 1500, missionId: '' };
    case 'ally':
      return { op: 'add', allyId: '', role: '', disposition: 'friendly' };
    case 'boon':
      return { target: 'team', heal: 0, wind: null, clear: '' };
    case 'delay':
      return { id: '', afterS: 20, then: [], cancelIfDead: '', onRaidEnd: 'drop' };
    case 'end':
      return { as: 'extracted' };
    case 'train':
      return { role: '', stat: 'wits', marks: 1 };
    case 'apply_effect':
      return { id: '', role: '' };
  }
}

/** An effect of `type` with exactly its one block (every other block removed), like `withTriggerAction`. */
export function newEffect(type: EffectType): EffectDef {
  return { type, [EFFECT_BLOCKS[type]]: effectBlockDefaults(type) } as EffectDef;
}

/** The role an effect targets, if it names one (for "roles bound" and "never the player"). */
export function effectRole(e: EffectDef): string | undefined {
  switch (e.type) {
    case 'injure':
      return e.injure?.role;
    case 'condition':
      return e.condition?.role;
    case 'kill':
      return e.kill?.role;
    case 'turn':
      return e.turn?.role;
    case 'recruit':
      return e.recruit?.role;
    case 'ally':
      return e.ally?.role;
    case 'train':
      return e.train?.role;
    case 'apply_effect':
      return e.applyEffect?.role || undefined;
    case 'boon': {
      const t = e.boon?.target ?? '';
      return t.startsWith('role:') ? t.slice(5) : undefined;
    }
    default:
      return undefined;
  }
}

/** A one-line summary for the outline ("kill companion", "delay turn_close 20 s"). */
export function effectSummary(e: EffectDef): string {
  const t = e.type ?? '';
  switch (t) {
    case 'give_items':
      return `give ${e.giveItems?.stacks?.length ?? 0} stack(s) → ${e.giveItems?.to ?? 'pack'}`;
    case 'take_items':
      return `take ${e.takeItems?.tag ? `tag ${e.takeItems.tag}` : `${e.takeItems?.stacks?.length ?? 0} stack(s)`}`;
    case 'trade':
      return `trade (${e.trade?.result ?? 'honest'})`;
    case 'reveal_stash':
      return `stash ${e.revealStash?.table ?? ''} @${e.revealStash?.anchor ?? ''}`;
    case 'delay':
      return `delay ${e.delay?.id ?? ''} ${e.delay?.afterS ?? 0} s → ${(e.delay?.then ?? []).map(effectSummary).join(', ')}`;
    case 'flag':
      return `flag ${e.flag?.key ?? ''}=${e.flag?.value ?? ''}`;
    case 'ally':
      return `ally ${e.ally?.op ?? 'add'} ${e.ally?.allyId ?? ''}`;
    case 'wave':
      return `wave ${e.wave?.size ?? 0}`;
    case 'spawn':
      return `spawn ${e.spawn?.count ?? 0} ${e.spawn?.zombieId ?? ''}`;
    case 'end':
      return `end ${e.end?.as ?? ''}`;
    case 'train':
      return `train ${e.train?.role ?? ''} ${(e.train?.stat ?? '').toUpperCase()} +${e.train?.marks ?? 0}`;
    case 'apply_effect':
      return `apply ${e.applyEffect?.id ?? ''}${e.applyEffect?.role ? ` on ${e.applyEffect.role}` : ''}`;
    default: {
      const role = effectRole(e);
      return role ? `${t} ${role}` : t;
    }
  }
}
